import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "@/api";
import {
  FileText, Upload, Search, Eye, Trash2, Download,
  RefreshCw, CheckSquare, Square, AlertTriangle, X,
} from "lucide-react";

const STATUS_COLORS = {
  approved: "bg-emerald-50 text-emerald-700",
  review: "bg-amber-50 text-amber-700",
  processing: "bg-blue-50 text-blue-700",
  error: "bg-red-50 text-red-700",
  rejected: "bg-red-50 text-red-700",
  pending: "bg-slate-100 text-slate-600",
};

/* ── CSV export helper ────────────────────────────────────────────────────── */
function exportToCsv(docs, schemas) {
  // Collect all unique extracted-field keys across selected docs
  const fieldKeys = new Set();
  docs.forEach(d => Object.keys(d.extracted_fields || {}).forEach(k => fieldKeys.add(k)));
  const extraCols = [...fieldKeys].sort();

  const schemaMap = Object.fromEntries(schemas.map(s => [s.id, s.name]));
  const header = ["filename", "schema", "status", "pages", "date", ...extraCols];

  const rows = docs.map(d => [
    `"${(d.original_filename || "").replace(/"/g, '""')}"`,
    `"${(schemaMap[d.schema_id] || "").replace(/"/g, '""')}"`,
    d.status || "",
    d.ocr_pages_count || "",
    (d.created_at || "").slice(0, 10),
    ...extraCols.map(k => {
      const val = (d.extracted_fields || {})[k];
      return val != null ? `"${String(val).replace(/"/g, '""')}"` : "";
    }),
  ]);

  const csv = [header.join(","), ...rows.map(r => r.join(","))].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `documents_export_${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

/* ── Confirm delete dialog ────────────────────────────────────────────────── */
function ConfirmDeleteModal({ count, onConfirm, onCancel, deleting }) {
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onCancel}>
      <div className="bg-white rounded-lg border border-slate-200 w-full max-w-sm" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-slate-200 flex items-center gap-2">
          <AlertTriangle size={15} className="text-red-500" />
          <h3 className="font-bold text-slate-900">Delete {count} document{count !== 1 ? "s" : ""}?</h3>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-sm text-slate-600">
            This will permanently remove {count === 1 ? "this document" : `these ${count} documents`} and all associated OCR data. This action cannot be undone.
          </p>
          <div className="flex gap-3">
            <button
              onClick={onCancel}
              className="flex-1 h-9 border border-slate-200 text-sm font-medium text-slate-700 rounded-md hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              data-testid="confirm-delete-button"
              onClick={onConfirm}
              disabled={deleting}
              className="flex-1 h-9 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {deleting ? <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Trash2 size={13} />}
              {deleting ? "Deleting…" : "Delete"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function DocumentList() {
  const [docs, setDocs] = useState([]);
  const [schemas, setSchemas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [schemaFilter, setSchemaFilter] = useState("");
  const [selected, setSelected] = useState(new Set()); // Set of doc IDs
  const [confirmDelete, setConfirmDelete] = useState(null); // null | "single:{id}" | "bulk"
  const [deleting, setDeleting] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    const [docsR, schemasR] = await Promise.all([
      api.get("/documents"),
      api.get("/schemas"),
    ]);
    setDocs(docsR.data);
    setSchemas(schemasR.data);
    setLoading(false);
  }, []);

  useEffect(() => { fetchAll().catch(() => setLoading(false)); }, [fetchAll]);

  const filtered = docs.filter(d => {
    const matchSearch = !search || d.original_filename.toLowerCase().includes(search.toLowerCase());
    const matchStatus = !statusFilter || d.status === statusFilter;
    const matchSchema = !schemaFilter || d.schema_id === schemaFilter;
    return matchSearch && matchStatus && matchSchema;
  });

  // Selection helpers
  const allFilteredSelected = filtered.length > 0 && filtered.every(d => selected.has(d.id));
  const someSelected = filtered.some(d => selected.has(d.id));

  const toggleAll = () => {
    if (allFilteredSelected) {
      setSelected(prev => {
        const next = new Set(prev);
        filtered.forEach(d => next.delete(d.id));
        return next;
      });
    } else {
      setSelected(prev => {
        const next = new Set(prev);
        filtered.forEach(d => next.add(d.id));
        return next;
      });
    }
  };

  const toggleOne = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  // Export selected docs as CSV
  const handleExport = () => {
    const selectedDocs = docs.filter(d => selected.has(d.id));
    exportToCsv(selectedDocs, schemas);
  };

  // Delete single
  const doDeleteSingle = async (docId) => {
    setDeleting(true);
    try {
      await api.delete(`/documents/${docId}`);
      setDocs(prev => prev.filter(d => d.id !== docId));
      setSelected(prev => { const next = new Set(prev); next.delete(docId); return next; });
    } finally {
      setDeleting(false);
      setConfirmDelete(null);
    }
  };

  // Bulk delete
  const doDeleteBulk = async () => {
    setDeleting(true);
    const ids = [...selected];
    try {
      await api.post("/documents/bulk-delete", { ids });
      setDocs(prev => prev.filter(d => !selected.has(d.id)));
      setSelected(new Set());
    } finally {
      setDeleting(false);
      setConfirmDelete(null);
    }
  };

  const handleDeleteConfirm = () => {
    if (!confirmDelete) return;
    if (confirmDelete === "bulk") doDeleteBulk();
    else doDeleteSingle(confirmDelete.replace("single:", ""));
  };

  const selectedCount = [...selected].filter(id => docs.some(d => d.id === id)).length;

  return (
    <div className="space-y-4">
      {/* Actions bar */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <div className="relative flex-1 max-w-xs">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              data-testid="document-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search documents..."
              className="w-full h-9 pl-8 pr-3 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-[#006080] bg-white"
            />
          </div>
          <select
            data-testid="status-filter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="h-9 px-3 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-[#006080] bg-white"
          >
            <option value="">All Statuses</option>
            {["pending", "processing", "review", "approved", "rejected", "error"].map(s => (
              <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
            ))}
          </select>
          <select
            data-testid="schema-filter"
            value={schemaFilter}
            onChange={(e) => setSchemaFilter(e.target.value)}
            className="h-9 px-3 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-[#006080] bg-white"
          >
            <option value="">All Schemas</option>
            {schemas.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={fetchAll} data-testid="refresh-documents" className="h-9 w-9 flex items-center justify-center border border-slate-200 rounded-md hover:bg-slate-50 text-slate-500">
            <RefreshCw size={14} />
          </button>
          <Link
            to="/documents/upload"
            data-testid="upload-document-button"
            className="flex items-center gap-2 h-9 px-4 bg-[#006080] hover:bg-[#004B66] text-white text-sm font-medium rounded-md transition-colors"
          >
            <Upload size={14} />
            Upload
          </Link>
        </div>
      </div>

      {/* Bulk action bar */}
      {selectedCount > 0 && (
        <div
          data-testid="bulk-action-bar"
          className="flex items-center justify-between gap-3 px-4 py-2.5 bg-[#006080] text-white rounded-lg"
        >
          <div className="flex items-center gap-2">
            <CheckSquare size={15} />
            <span className="text-sm font-medium">{selectedCount} document{selectedCount !== 1 ? "s" : ""} selected</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="bulk-export-button"
              onClick={handleExport}
              className="flex items-center gap-1.5 h-7 px-3 text-xs font-medium bg-white/20 hover:bg-white/30 rounded transition-colors"
            >
              <Download size={12} /> Export CSV
            </button>
            <button
              data-testid="bulk-delete-button"
              onClick={() => setConfirmDelete("bulk")}
              className="flex items-center gap-1.5 h-7 px-3 text-xs font-medium bg-red-500/80 hover:bg-red-500 rounded transition-colors"
            >
              <Trash2 size={12} /> Delete
            </button>
            <button
              data-testid="bulk-clear-selection"
              onClick={() => setSelected(new Set())}
              className="h-7 w-7 flex items-center justify-center hover:bg-white/20 rounded transition-colors"
            >
              <X size={14} />
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-6 h-6 border-2 border-[#006080] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16">
            <FileText size={32} className="text-slate-300 mx-auto mb-3" />
            <p className="text-sm text-slate-500">No documents found</p>
            <Link to="/documents/upload" className="text-xs text-[#006080] hover:underline mt-1 inline-block">Upload a document</Link>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                {/* Select-all checkbox */}
                <th className="w-10 px-4 py-3">
                  <button
                    data-testid="select-all-checkbox"
                    onClick={toggleAll}
                    className="text-slate-400 hover:text-[#006080] transition-colors"
                  >
                    {allFilteredSelected
                      ? <CheckSquare size={15} className="text-[#006080]" />
                      : someSelected
                        ? <CheckSquare size={15} className="text-slate-300" />
                        : <Square size={15} />}
                  </button>
                </th>
                <th className="text-left text-xs font-bold uppercase tracking-wide text-slate-500 px-4 py-3">File</th>
                <th className="text-left text-xs font-bold uppercase tracking-wide text-slate-500 px-4 py-3">Schema</th>
                <th className="text-left text-xs font-bold uppercase tracking-wide text-slate-500 px-4 py-3">Pages</th>
                <th className="text-left text-xs font-bold uppercase tracking-wide text-slate-500 px-4 py-3">Source</th>
                <th className="text-left text-xs font-bold uppercase tracking-wide text-slate-500 px-4 py-3">Status</th>
                <th className="text-left text-xs font-bold uppercase tracking-wide text-slate-500 px-4 py-3">Date</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {filtered.map((doc, i) => {
                const schema = schemas.find(s => s.id === doc.schema_id);
                const isSelected = selected.has(doc.id);
                return (
                  <tr
                    key={doc.id}
                    data-testid={`document-row-${doc.id}`}
                    className={`border-b border-slate-100 transition-colors ${isSelected ? "bg-[#E5F0F2]/40" : i % 2 === 1 ? "bg-slate-50/50 hover:bg-slate-50" : "hover:bg-slate-50"}`}
                  >
                    <td className="px-4 py-3">
                      <button
                        data-testid={`select-doc-${doc.id}`}
                        onClick={() => toggleOne(doc.id)}
                        className="text-slate-400 hover:text-[#006080] transition-colors"
                      >
                        {isSelected ? <CheckSquare size={15} className="text-[#006080]" /> : <Square size={15} />}
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <FileText size={14} className="text-slate-400 flex-shrink-0" />
                        <span className="font-medium text-slate-900 truncate max-w-[200px]">{doc.original_filename}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-500">{schema?.name || <span className="text-slate-300">—</span>}</td>
                    <td className="px-4 py-3 text-slate-500 text-right">{doc.ocr_pages_count || "—"}</td>
                    <td className="px-4 py-3">
                      <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">{doc.upload_source}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${STATUS_COLORS[doc.status] || "bg-slate-100 text-slate-600"}`}>
                        {doc.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-500 text-xs">{doc.created_at?.slice(0, 10)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <Link
                          to={`/documents/${doc.id}`}
                          data-testid={`view-doc-${doc.id}`}
                          className="p-1.5 hover:bg-[#E5F0F2] rounded text-slate-400 hover:text-[#006080] transition-colors"
                        >
                          <Eye size={13} />
                        </Link>
                        <button
                          data-testid={`delete-doc-${doc.id}`}
                          onClick={() => setConfirmDelete(`single:${doc.id}`)}
                          className="p-1.5 hover:bg-red-50 rounded text-slate-400 hover:text-red-500 transition-colors"
                          title="Delete document"
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Confirm delete modal */}
      {confirmDelete && (
        <ConfirmDeleteModal
          count={confirmDelete === "bulk" ? selectedCount : 1}
          onConfirm={handleDeleteConfirm}
          onCancel={() => setConfirmDelete(null)}
          deleting={deleting}
        />
      )}
    </div>
  );
}
